import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProjectCard from '../components/ProjectCard';
import Contacts from '../components/Contacts';
import { fetchData, getMediaUrl } from '../api/api';
import '../styles/Projects.css';

const PER_PAGE = 12;

function Projects() {
    const [projectData, setProjectData] = useState([]);
    const [currentPage, setCurrentPage]  = useState(1);
    const [loading, setLoading]          = useState(true);
    const [error, setError]              = useState(null);
    const [filter, setFilter]            = useState('all');
    const gridRef                        = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const data = await fetchData('/projects');
                const list = (data.message || []).map(p => ({
                    id:           p.id,
                    title:        p.name,
                    description:  p.description,
                    thumbnail:    getMediaUrl(p.thumbnail),
                    link:         p.link,
                    date_created: p.date_created,
                    status:       p.status,
                    // category comes from the first segment of the link field
                    category:     p.link?.split('/')[0]?.toUpperCase() || 'OTHER',
                }));
                setProjectData(list);
                setError(null);
            } catch (err) {
                console.error('Error fetching projects:', err);
                setError('Failed to load projects. Please try again later.');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // ── FILTER ──
    const categories = ['all', ...new Set(projectData.map(p => p.category).filter(Boolean))];
    const filtered   = filter === 'all' ? projectData : projectData.filter(p => p.category === filter);

    // ── PAGINATION ──
    const totalPages    = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    const safePage      = Math.min(currentPage, totalPages);
    const pageProjects  = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

    const goTo = (n) => {
        setCurrentPage(n);
        // Scroll grid into view (below the fixed nav)
        setTimeout(() => {
            gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
    };

    const setFilterReset = (f) => { setFilter(f); setCurrentPage(1); };

    // Smart page number window (show at most 5 page buttons)
    const pageNums = (() => {
        const range = 2;
        const nums  = [];
        for (let i = Math.max(1, safePage - range); i <= Math.min(totalPages, safePage + range); i++) {
            nums.push(i);
        }
        return nums;
    })();

    // ── ERROR ──
    if (error) {
        return (
            <>
                <Header />
                <div className="projects-page">
                    <div className="projects-state">
                        <p className="state-icon">⚠</p>
                        <p className="state-msg">{error}</p>
                        <button className="btn btn-color-2" onClick={() => window.location.reload()}>
                            Retry
                        </button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />

            <div className="projects-page">

                {/* ── HERO ── */}
                <div className="projects-hero">
                    <p className="section__text__p1">Browse the archive</p>
                    <h1 className="projects-page-title">
                        All <span className="spark-word">Projects</span>
                    </h1>
                    <p className="projects-hero-sub">
                        {loading
                            ? '...'
                            : `${filtered.length} project${filtered.length !== 1 ? 's' : ''}`}
                        {filter !== 'all' && (
                            <span className="filter-tag"> / {filter}</span>
                        )}
                    </p>
                </div>

                {/* ── FILTER BAR ── */}
                {!loading && categories.length > 1 && (
                    <div className="filter-bar">
                        {categories.map(cat => (
                            <button
                                key={cat}
                                className={`filter-btn${filter === cat ? ' active' : ''}`}
                                onClick={() => setFilterReset(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                )}

                {/* ── LOADING ── */}
                {loading && (
                    <div className="projects-state">
                        <div className="loader">
                            <span /><span /><span />
                        </div>
                        <p className="state-msg">
                            Loading projects<span className="cursor-blink" />
                        </p>
                    </div>
                )}

                {/* ── GRID ── */}
                {!loading && (
                    <>
                        <div className="projects-grid" ref={gridRef}>
                            {pageProjects.length > 0 ? (
                                pageProjects.map((project, i) => (
                                    <ProjectCard
                                        key={project.id}
                                        project={project}
                                        index={i}
                                    />
                                ))
                            ) : (
                                <div className="projects-empty">
                                    <p>No projects in this category yet.</p>
                                </div>
                            )}
                        </div>

                        {/* ── PAGINATION ── */}
                        {totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    className="pag-btn pag-arrow"
                                    onClick={() => goTo(1)}
                                    disabled={safePage === 1}
                                    title="First page"
                                >{'<<'}</button>

                                <button
                                    className="pag-btn pag-arrow"
                                    onClick={() => goTo(safePage - 1)}
                                    disabled={safePage === 1}
                                    title="Previous page"
                                >{'<'}</button>

                                {pageNums[0] > 1 && (
                                    <>
                                        <button className="pag-btn" onClick={() => goTo(1)}>1</button>
                                        {pageNums[0] > 2 && <span className="pag-ellipsis">…</span>}
                                    </>
                                )}

                                {pageNums.map(n => (
                                    <button
                                        key={n}
                                        className={`pag-btn${n === safePage ? ' pag-active' : ''}`}
                                        onClick={() => goTo(n)}
                                    >
                                        {n}
                                    </button>
                                ))}

                                {pageNums[pageNums.length - 1] < totalPages && (
                                    <>
                                        {pageNums[pageNums.length - 1] < totalPages - 1 && (
                                            <span className="pag-ellipsis">…</span>
                                        )}
                                        <button className="pag-btn" onClick={() => goTo(totalPages)}>
                                            {totalPages}
                                        </button>
                                    </>
                                )}

                                <button
                                    className="pag-btn pag-arrow"
                                    onClick={() => goTo(safePage + 1)}
                                    disabled={safePage === totalPages}
                                    title="Next page"
                                >{'>'}</button>

                                <button
                                    className="pag-btn pag-arrow"
                                    onClick={() => goTo(totalPages)}
                                    disabled={safePage === totalPages}
                                    title="Last page"
                                >{'>>'}</button>
                            </div>
                        )}
                    </>
                )}
            </div>

            <Contacts />
            <Footer />
        </>
    );
}

export default Projects;